import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Trophy,
  Users,
  Settings,
  CalendarDays,
  DollarSign,
  Award,
  Plus,
  Search,
  Shield,
  ShieldCheck,
  ArrowRight,
  Edit,
  Trash2,
  X,
  CreditCard,
  AlertCircle,
  Hash,
  Trash,
  CheckCircle2,
  XCircle,
  UserCheck,
  LayoutList,
  User,
  Phone,
  Repeat,
  ChevronDown,
  Minus,
  Lock,
  Info
} from 'lucide-react';
import { doc, updateDoc, setDoc, collection, writeBatch } from 'firebase/firestore';
import { Tournament, Team, Config, Player, RankingConfig, UserProfile } from '../../types';
import { ALL_CITIES } from '../../data/locations';
import { handleFirestoreError } from '../../lib/errorHandlers';
import { formatPairName } from '../../lib/utils';
import { SearchableTeamSelect } from '../common/SearchableTeamSelect';
import { PairAvatar } from '../common/PairAvatar';
import { SearchablePlayerSelect } from '../common/SearchablePlayerSelect';
import { PlayerDetailModal } from '../player/PlayerDetailModal';
import { auth } from '../../lib/firebase';
import { logActivity } from '../../services/activityLogger';

interface AdminTournamentLobbyProps {
  t: Tournament;
  sedeName?: string;
  allTeams: Team[];
  players: Player[];
  sedesPublicas?: UserProfile[];
  db: any;
  getCollectionPath: (col: string) => string;
  config: Config;
  rankingConfig: RankingConfig;
  isEditingInfo?: boolean;
  setIsEditingInfo?: (val: boolean) => void;
  showExitConfirm?: boolean;
  setShowExitConfirm?: (val: boolean) => void;
  onHasChangesChange?: (val: boolean) => void;
  saveTriggerRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  discardTriggerRef?: React.MutableRefObject<(() => void) | null>;
}
export function AdminTournamentLobby({
  t,
  allTeams,
  players,
  sedesPublicas = [],
  db,
  getCollectionPath,
  config,
  rankingConfig,
  isEditingInfo: isEditingInfoProp,
  setIsEditingInfo: setIsEditingInfoProp,
  showExitConfirm: showExitConfirmProp,
  setShowExitConfirm: setShowExitConfirmProp,
  onHasChangesChange,
  saveTriggerRef,
  discardTriggerRef,
  sedeName
}: AdminTournamentLobbyProps) {
  const lobbyRef = useRef<HTMLDivElement>(null);
  const getCategoryRank = (cat: string = ''): number => {
    const c = cat.toLowerCase();
    if (c.includes('1ra') || c.includes('1era')) return 1;
    if (c.includes('2da')) return 2;
    if (c.includes('3ra')) return 3;
    if (c.includes('4ta')) return 4;
    if (c.includes('5ta')) return 5;
    if (c.includes('6ta')) return 6;
    if (c.includes('7ma')) return 7;
    if (c.includes('8va')) return 8;
    if (c.includes('suma')) {
      const match = c.match(/\d+/);
      return match ? parseInt(match[0]) : 15;
    }
    if (c.includes('veterano')) return 10;
    return 20;
  };

  const validateTeamRegistration = (team: Team) => {
    const errors: string[] = [];
    const p1 = players.find(p => p.id === team.player1Id);
    const p2 = players.find(p => p.id === team.player2Id);

    if (!p1 || !p2) return { valid: true, errors: [] }; // Skip if players not found yet

    // Gender check
    const tournamentGender = t.gender?.toLowerCase() || 'masculino';
    const p1Gender = p1.gender?.toLowerCase() || 'masculino';
    const p2Gender = p2.gender?.toLowerCase() || 'masculino';

    if (tournamentGender === 'masculino') {
      if (p1Gender !== 'masculino' || p2Gender !== 'masculino') {
        errors.push(`Este torneo es solo para Caballeros`);
      }
    } else if (tournamentGender === 'femenino') {
      if (p1Gender !== 'femenino' || p2Gender !== 'femenino') {
        errors.push(`Este torneo es solo para Damas`);
      }
    } else if (tournamentGender === 'mixto') {
      const hasMale = p1Gender === 'masculino' || p2Gender === 'masculino';
      const hasFemale = p1Gender === 'femenino' || p2Gender === 'femenino';
      if (!(hasMale && hasFemale) || p1Gender === p2Gender) {
        errors.push('La pareja mixta debe estar formada por un Masculino y un Femenino');
      }
    }

    // Category check
    const isFreeCategory = t.category?.toLowerCase().includes('sin restriccion') ||
      t.category?.toLowerCase().includes('sin restricción') ||
      t.category?.toLowerCase().includes('libre');

    const isSumaCategory = t.category?.toLowerCase().startsWith('suma');

    if (!isFreeCategory) {
      const tourneyRank = getCategoryRank(t.category);
      
      const p1Rank = p1.category ? getCategoryRank(p1.category) : tourneyRank;
      const p2Rank = p2.category ? getCategoryRank(p2.category) : tourneyRank;

      if (isSumaCategory) {
        const sumLimitMatch = t.category?.match(/\d+/);
        const sumLimit = sumLimitMatch ? parseInt(sumLimitMatch[0], 10) : 13;
        
        const effectiveP1Rank = p1.category ? p1Rank : Math.max(1, sumLimit - p2Rank);
        const effectiveP2Rank = p2.category ? p2Rank : Math.max(1, sumLimit - effectiveP1Rank);
        const pairSum = effectiveP1Rank + effectiveP2Rank;
        
        if (pairSum < sumLimit && p1.category && p2.category) {
          errors.push(`La suma de sus categorías (${p1Rank} + ${p2Rank} = ${pairSum}) no alcanza el mínimo de ${sumLimit} permitido.`);
        }
      } else {
        if (p1Rank < tourneyRank && p1.category) errors.push(`Categoría superior: ${p1.name} (${p1.category}) > ${t.category}`);
        if (p2Rank < tourneyRank && p2.category) errors.push(`Categoría superior: ${p2.name} (${p2.category}) > ${t.category}`);
      }
    }

    return { valid: errors.length === 0, errors };
  };

  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [localIsEditingInfo, localSetIsEditingInfo] = useState(false);
  const isEditingInfo = isEditingInfoProp !== undefined ? isEditingInfoProp : localIsEditingInfo;
  const setIsEditingInfo = setIsEditingInfoProp !== undefined ? setIsEditingInfoProp : localSetIsEditingInfo;
  const [editData, setEditData] = useState({
    name: t.name,
    type: t.type,
    category: t.category || '5ta',
    gender: t.gender || 'masculino',
    maxTeams: t.maxTeams || 16,
    entryFee: t.entryFee || 0,
    startDate: t.startDate || '',
    description: t.description || '',
    rulesText: t.rulesText || '',
    rewards: t.rewards || [],
    locationMode: t.locationMode || 'tbd',
    locationCity: t.locationCity || '',
    locationClubIds: t.locationClubIds || [],
  });

  const [searchTeamId, setSearchTeamId] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [placementMode, setPlacementMode] = useState<'auto' | 'manual'>('auto');
  const [isPlacementOpen, setIsPlacementOpen] = useState(false);
  const [manualPlacement, setManualPlacement] = useState<Record<string, string>>({}); // { "match_0_0": "teamId" }

  // Couple Builder State
  const [errorModal, setErrorModal] = useState<{ show: boolean, message: string }>({ show: false, message: '' });
  const [showCoupleBuilder, setShowCoupleBuilder] = useState(false);
  const [p1Id, setP1Id] = useState('');
  const [p2Id, setP2Id] = useState('');
  const [p1PhoneInput, setP1PhoneInput] = useState('');
  const [p2PhoneInput, setP2PhoneInput] = useState('');
  const [isCreatingCouple, setIsCreatingCouple] = useState(false);

  useEffect(() => {
    if (p1Id) {
      const p = players.find(p => p.id === p1Id);
      if (p && p.phone) setP1PhoneInput('');
    }
  }, [p1Id, players]);

  useEffect(() => {
    if (p2Id) {
      const p = players.find(p => p.id === p2Id);
      if (p && p.phone) setP2PhoneInput('');
    }
  }, [p2Id, players]);

  const showError = (message: string) => setErrorModal({ show: true, message });

  // Guest Builder State
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [isCreatingGuest, setIsCreatingGuest] = useState(false);

  const [numZones, setNumZones] = useState(2);
  const [qualifiersPerZone, setQualifiersPerZone] = useState(2);
  const [doubleRound, setDoubleRound] = useState(false);
  const [hasPlayoffs, setHasPlayoffs] = useState(true);
  const [schedulePlayMode, setSchedulePlayMode] = useState<'open' | 'restricted'>('open');
  const [scheduleDaysPerRound, setScheduleDaysPerRound] = useState(7);
  const [scheduleTimeUnit, setScheduleTimeUnit] = useState<'days' | 'hours' | 'minutes'>('days');
  const [advancedDatesAllowed, setAdvancedDatesAllowed] = useState<number | 'Todos'>(t.advancedDatesAllowed ?? 2);
  const advancedScheduleRestrictions = schedulePlayMode === 'restricted';
  const setAdvancedScheduleRestrictions = (val: boolean) => {
    setSchedulePlayMode(val ? 'restricted' : 'open');
  };
  const [showStartConfirmation, setShowStartConfirmation] = useState(false);
  const [officialStartDate, setOfficialStartDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow.getTime();
  });
  const [localShowExitConfirm, localSetShowExitConfirm] = useState(false);
  const showExitConfirm = showExitConfirmProp !== undefined ? showExitConfirmProp : localShowExitConfirm;
  const setShowExitConfirm = setShowExitConfirmProp !== undefined ? setShowExitConfirmProp : localSetShowExitConfirm;


  const registeredTeams = allTeams.filter(team => t.registeredTeamIds?.includes(team.id));
  // Sort registered teams according to the order in t.registeredTeamIds
  const sortedRegisteredTeams = (t.registeredTeamIds || [])
    .map(id => registeredTeams.find(rt => rt.id === id))
    .filter(Boolean) as Team[];

  const isFull = (t.registeredTeamIds?.length || 0) >= (t.maxTeams || 16);

  const hasChanges = JSON.stringify({
    ...editData,
    maxTeams: Number(editData.maxTeams),
    entryFee: Number(editData.entryFee),
  }) !== JSON.stringify({
    name: t.name,
    type: t.type,
    category: t.category || '5ta',
    gender: t.gender || 'masculino',
    maxTeams: Number(t.maxTeams || 16),
    entryFee: Number(t.entryFee || 0),
    startDate: t.startDate || '',
    description: t.description || '',
    rulesText: t.rulesText || '',
    rewards: t.rewards || [],
    locationMode: t.locationMode || 'tbd',
    locationCity: t.locationCity || '',
    locationClubIds: t.locationClubIds || [],
  });

  const handleUpdateInfo = async () => {
    await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), {
      ...editData,
      maxTeams: Number(editData.maxTeams),
      entryFee: Number(editData.entryFee),
      type: editData.type,
      gender: editData.gender,
      rulesText: editData.rulesText,
      description: editData.description,
      rewards: editData.rewards,
      qualifiersPerZone: Number(qualifiersPerZone),
      locationMode: editData.locationMode,
      locationCity: editData.locationMode === 'tbd' ? editData.locationCity : '',
      locationClubIds: editData.locationMode === 'clubs' ? editData.locationClubIds : [],
    });
    setIsEditingInfo(false);
  };

  useEffect(() => {
    onHasChangesChange?.(hasChanges);
  }, [hasChanges, onHasChangesChange]);

  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = handleUpdateInfo;
    }
    if (discardTriggerRef) {
      discardTriggerRef.current = () => {
        setIsEditingInfo(false);
        setShowExitConfirm(false);
        setEditData({
          name: t.name,
          type: t.type,
          category: t.category || '5ta',
          gender: t.gender || 'masculino',
          maxTeams: t.maxTeams || 16,
          entryFee: t.entryFee || 0,
          startDate: t.startDate || '',
          description: t.description || '',
          rulesText: t.rulesText || '',
          rewards: t.rewards || [],
          locationMode: t.locationMode || 'tbd',
          locationCity: t.locationCity || '',
          locationClubIds: t.locationClubIds || [],
        });
      };
    }
    return () => {
      if (saveTriggerRef) saveTriggerRef.current = null;
      if (discardTriggerRef) discardTriggerRef.current = null;
    };
  }, [editData, t, handleUpdateInfo, saveTriggerRef, discardTriggerRef, setIsEditingInfo, setShowExitConfirm]);

  const addTeamToLobby = async (teamId: string) => {
    if (!teamId || t.registeredTeamIds?.includes(teamId)) return;
    if (isFull) {
      showError("El torneo ya alcanzó el cupo máximo de equipos.");
      return;
    }

    const team = allTeams.find(tm => tm.id === teamId);
    if (team) {
      const { valid, errors } = validateTeamRegistration(team);
      if (!valid) {
        if (!await window.confirm(`AVISO DE CATEGORÍA/GÉNERO:\n\n${errors.join('\n')}\n\n¿Deseas ignorar esta advertencia e inscribirlos de todas formas?`)) {
          return;
        }
      }
    }

    const newList = [...(t.registeredTeamIds || []), teamId];
    await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), {
      registeredTeamIds: newList
    });
    setSearchTeamId('');
  };

  const handleCreateCoupleAndRegister = async () => {
    if (!p1Id || !p2Id || p1Id === p2Id) {
      showError("Selecciona dos jugadores diferentes.");
      return;
    }

    const p1Check = players.find(p => p.id === p1Id);
    const p2Check = players.find(p => p.id === p2Id);
    
    if (p1Check && !p1Check.phone && !p1PhoneInput.trim()) {
      if (!await window.confirm(`¿Estás seguro que deseas registrar a ${p1Check.name} sin un número de teléfono?`)) {
        return;
      }
    }
    if (p2Check && !p2Check.phone && !p2PhoneInput.trim()) {
      if (!await window.confirm(`¿Estás seguro que deseas registrar a ${p2Check.name} sin un número de teléfono?`)) {
        return;
      }
    }

    if (isFull) {
      showError("El torneo ya alcanzó el cupo máximo de parejas.");
      return;
    }

    const p1 = players.find(p => p.id === p1Id);
    const p2 = players.find(p => p.id === p2Id);
    if (p1 && p2) {
      const { valid, errors } = validateTeamRegistration({ player1Id: p1Id, player2Id: p2Id } as Team);
      if (!valid) {
        if (!await window.confirm(`AVISO DE CATEGORÍA/GÉNERO:\n\n${errors.join('\n')}\n\n¿Deseas ignorar esta advertencia e inscribirlos de todas formas?`)) {
          return;
        }
      }
    }


    setIsCreatingCouple(true);
    try {
      const p1 = players.find(p => p.id === p1Id);
      const p2 = players.find(p => p.id === p2Id);

      if (!p1 || !p2) throw new Error("Jugadores no encontrados");

      // Inherit phone from players (pick one at random from available ones)
      const availablePhones = [p1.phone, p2.phone].filter(Boolean) as string[];
      const randomPhone = availablePhones.length > 0
        ? availablePhones[Math.floor(Math.random() * availablePhones.length)]
        : '';

      // Save new phones to players
      let finalP1Phone = p1.phone;
      let finalP2Phone = p2.phone;

      if (p1 && !p1.phone && p1PhoneInput.trim()) {
        await updateDoc(doc(db, getCollectionPath('players'), p1.id), { phone: p1PhoneInput.trim() }).catch(() => {});
        await updateDoc(doc(db, 'users', p1.id), { phone: p1PhoneInput.trim() }).catch(() => {});
        finalP1Phone = p1PhoneInput.trim();
      }
      if (p2 && !p2.phone && p2PhoneInput.trim()) {
        await updateDoc(doc(db, getCollectionPath('players'), p2.id), { phone: p2PhoneInput.trim() }).catch(() => {});
        await updateDoc(doc(db, 'users', p2.id), { phone: p2PhoneInput.trim() }).catch(() => {});
        finalP2Phone = p2PhoneInput.trim();
      }

      // Inherit phone from players
      const finalAvailablePhones = [finalP1Phone, finalP2Phone].filter(Boolean) as string[];
      const finalRandomPhone = finalAvailablePhones.length > 0
        ? finalAvailablePhones[Math.floor(Math.random() * finalAvailablePhones.length)]
        : '';

      // Always create a new tournament-specific pair
      const teamId = doc(collection(db, getCollectionPath('teams'))).id;
      const teamRef = doc(db, getCollectionPath('teams'), teamId);

      await setDoc(teamRef, {
        id: teamId,
        name: `${p1.name} & ${p2.name}`,
        player1Id: p1Id,
        player2Id: p2Id,
        phone: finalRandomPhone,
        organizerId: t.organizerId,
        createdAt: Date.now()
      });

      // Add to tournament
      const newList = [...(t.registeredTeamIds || []), teamId];
      await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), {
        registeredTeamIds: newList
      });

      const organizerLabel = sedeName || 'El organizador';

      // Notification for player1
      if (!p1.isGuest) {
        const notifId1 = doc(collection(db, 'notifications')).id;
        await setDoc(doc(db, 'notifications', notifId1), {
          id: notifId1,
          userId: p1.id,
          type: 'tournament_invitation',
          title: `Pre-inscripción en ${t.name}`,
          body: `El organizador ${organizerLabel} te ha pre-inscripto en el torneo ${t.name} junto con ${p2.name}.`,
          matchId: t.id,
          read: false,
          createdAt: Date.now()
        }).catch(err => console.error("Error creating notification for p1:", err));
      }

      // Notification for player2
      if (!p2.isGuest) {
        const notifId2 = doc(collection(db, 'notifications')).id;
        await setDoc(doc(db, 'notifications', notifId2), {
          id: notifId2,
          userId: p2.id,
          type: 'tournament_invitation',
          title: `Pre-inscripción en ${t.name}`,
          body: `El organizador ${organizerLabel} te ha pre-inscripto en el torneo ${t.name} junto con ${p1.name}.`,
          matchId: t.id,
          read: false,
          createdAt: Date.now()
        }).catch(err => console.error("Error creating notification for p2:", err));
      }

      // Reset state
      setP1Id('');
      setP2Id('');
      setP1PhoneInput('');
      setP2PhoneInput('');
      setShowCoupleBuilder(false);
    } catch (error) {
      console.error("Error creating couple:", error);
      showError("Ocurrió un error al crear la pareja.");
    } finally {
      setIsCreatingCouple(false);
    }
  };

  const handleCreateGuest = async () => {
    if (!guestName.trim()) return;
    setIsCreatingGuest(true);
    try {
      const newPlayerId = doc(collection(db, getCollectionPath('players'))).id;
      const newPlayer: Player = {
        id: newPlayerId,
        name: guestName.trim(), // Tag provided by isGuest
        category: t.category || '5ta',
        gender: t.gender || 'masculino',
        phone: guestPhone.trim(),
        isGuest: true,
        guestTournamentId: t.id,
        createdAt: Date.now(),
      };
      await setDoc(doc(db, getCollectionPath('players'), newPlayerId), newPlayer);
      setGuestName('');
      setGuestPhone('');
      setShowAddGuest(false);
    } catch (e) {
      console.error(e);
      showError('Error creando invitado');
    } finally {
      setIsCreatingGuest(false);
    }
  };

  const moveTeam = async (index: number, direction: 'up' | 'down') => {
    if (!t.registeredTeamIds) return;
    const newList = [...t.registeredTeamIds];
    const newIdx = direction === 'up' ? index - 1 : index + 1;
    if (newIdx < 0 || newIdx >= newList.length) return;
    [newList[index], newList[newIdx]] = [newList[newIdx], newList[index]];
    await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), {
      registeredTeamIds: newList
    });
  };

  const removeTeamFromLobby = async (teamId: string) => {
    if (await window.confirm("¿Estás seguro de que quieres remover a esta pareja del torneo?")) {
      const newList = (t.registeredTeamIds || []).filter(id => id !== teamId);
      await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), {
        registeredTeamIds: newList
      });

      // Send cancellation notification to the team players
      const team = allTeams.find(tm => tm.id === teamId);
      if (team) {
        const p1 = players.find(p => p.id === team.player1Id);
        const p2 = players.find(p => p.id === team.player2Id);

        if (p1 && !p1.isGuest) {
          const notifId = doc(collection(db, 'notifications')).id;
          await setDoc(doc(db, 'notifications', notifId), {
            id: notifId,
            userId: p1.id,
            type: 'system',
            title: `Inscripción Cancelada`,
            body: `Se ha cancelado tu pre-inscripción al torneo ${t.name}.`,
            matchId: t.id,
            read: false,
            createdAt: Date.now()
          }).catch(console.error);
        }

        if (p2 && !p2.isGuest) {
          const notifId = doc(collection(db, 'notifications')).id;
          await setDoc(doc(db, 'notifications', notifId), {
            id: notifId,
            userId: p2.id,
            type: 'system',
            title: `Inscripción Cancelada`,
            body: `Se ha cancelado tu pre-inscripción al torneo ${t.name}.`,
            matchId: t.id,
            read: false,
            createdAt: Date.now()
          }).catch(console.error);
        }
      }
    }
  };

  const handleApproveRegistration = async (teamId: string) => {
    const team = allTeams.find(tm => tm.id === teamId);
    if (!team) return;

    try {
      const tournamentRef = doc(db, getCollectionPath('tournaments'), t.id);
      const pendingList = (t.pendingTeamIds || []).filter(id => id !== teamId);
      const registeredList = [...(t.registeredTeamIds || []), teamId];

      await updateDoc(tournamentRef, {
        pendingTeamIds: pendingList,
        registeredTeamIds: registeredList
      }).catch(err => handleFirestoreError(err, 'update', `tournaments/${t.id}`));

      // Notification for player1
      if (team.player1Id) {
        const notifId1 = doc(collection(db, 'notifications')).id;
        await setDoc(doc(db, 'notifications', notifId1), {
          id: notifId1,
          userId: team.player1Id,
          type: 'tournament_invitation',
          title: '¡Pareja Aceptada!',
          body: `Tu pareja ha sido aceptada en ${t.name}. ¡Bienvenidos al torneo!`,
          matchId: t.id,
          read: false,
          createdAt: Date.now()
        }).catch(err => handleFirestoreError(err, 'create', `notifications/${notifId1}`));
      }

      // Notification for player2
      if (team.player2Id) {
        const notifId2 = doc(collection(db, 'notifications')).id;
        await setDoc(doc(db, 'notifications', notifId2), {
          id: notifId2,
          userId: team.player2Id,
          type: 'tournament_invitation',
          title: '¡Pareja Aceptada!',
          body: `Tu pareja ha sido aceptada en ${t.name}. ¡Bienvenidos al torneo!`,
          matchId: t.id,
          read: false,
          createdAt: Date.now()
        }).catch(err => handleFirestoreError(err, 'create', `notifications/${notifId2}`));
      }
    } catch (err) {
      console.error("Error approving registration:", err);
    }
  };

  const handleRejectRegistration = async (teamId: string) => {
    try {
      const tournamentRef = doc(db, getCollectionPath('tournaments'), t.id);
      const pendingList = (t.pendingTeamIds || []).filter(id => id !== teamId);

      await updateDoc(tournamentRef, {
        pendingTeamIds: pendingList
      }).catch(err => handleFirestoreError(err, 'update', `tournaments/${t.id}`));

      const team = allTeams.find(tm => tm.id === teamId);
      if (!team) return;

      // Notification for player1
      if (team.player1Id) {
        const notifId1 = doc(collection(db, 'notifications')).id;
        await setDoc(doc(db, 'notifications', notifId1), {
          id: notifId1,
          userId: team.player1Id,
          type: 'tournament_invitation',
          title: 'Pre-inscripción rechazada',
          body: `La solicitud de tu pareja para ${t.name} no ha sido aceptada.`,
          matchId: t.id,
          read: false,
          createdAt: Date.now()
        }).catch(err => handleFirestoreError(err, 'create', `notifications/${notifId1}`));
      }

      if (team.player2Id) {
        const notifId2 = doc(collection(db, 'notifications')).id;
        await setDoc(doc(db, 'notifications', notifId2), {
          id: notifId2,
          userId: team.player2Id,
          type: 'tournament_invitation',
          title: 'Pre-inscripción rechazada',
          body: `La solicitud de tu pareja para ${t.name} no ha sido aceptada.`,
          matchId: t.id,
          read: false,
          createdAt: Date.now()
        }).catch(err => handleFirestoreError(err, 'create', `notifications/${notifId2}`));
      }
    } catch (err) {
      console.error("Error rejecting registration:", err);
    }
  };

  const toggleTeamApproval = async (teamId: string) => {
    try {
      const tournamentRef = doc(db, getCollectionPath('tournaments'), t.id);
      const approvedList = t.approvedTeamIds || [];
      const newList = approvedList.includes(teamId)
        ? approvedList.filter(id => id !== teamId)
        : [...approvedList, teamId];

      await updateDoc(tournamentRef, {
        approvedTeamIds: newList
      }).catch(err => handleFirestoreError(err, 'update', `tournaments/${t.id}`));
    } catch (err) {
      console.error("Error toggling team approval:", err);
    }
  };

  const approveAllTeams = async () => {
    try {
      const tournamentRef = doc(db, getCollectionPath('tournaments'), t.id);
      const allApproved = (t.registeredTeamIds || []).length > 0 &&
        (t.registeredTeamIds || []).every(id => t.approvedTeamIds?.includes(id));

      await updateDoc(tournamentRef, {
        approvedTeamIds: allApproved ? [] : (t.registeredTeamIds || [])
      }).catch(err => handleFirestoreError(err, 'update', `tournaments/${t.id}`));
    } catch (err) {
      console.error("Error approving all teams:", err);
    }
  };

  // Lógica de inicio de torneo idéntica a AutoTournamentWizard pero usando registrados
  const startTournament = async (forcedTeams?: string[]) => {
    console.log('[DEBUG] Start tournament called');
    if (sortedRegisteredTeams.length < 2) {
      showError("Se necesitan al menos 2 equipos para comenzar.");
      return;
    }

    setIsStarting(true);
    let finalTeams: any[] = [];

    if (forcedTeams) {
      // En posicionamiento manual, forcedTeams ya incluye los IDs en orden, incluyendo "BYE"
      finalTeams = forcedTeams.map(id => {
        if (id === 'BYE') return { id: 'BYE', name: 'PASE DIRECTO' };
        return allTeams.find(tm => tm.id === id);
      }).filter(Boolean);
    } else {
      finalTeams = [...sortedRegisteredTeams];
      if (placementMode === 'auto') {
        finalTeams = finalTeams.sort(() => Math.random() - 0.5);
      }
    }

    if (t.type === 'liga') {
      const zones = Array.from({ length: numZones }).map((_, i) => ({
        name: `Zona ${String.fromCharCode(65 + i)}`,
        teams: [] as any[],
      }));

      // Usamos la lista de finalTeams (que ya fue mezclada si estamos en modo automático)
      finalTeams.forEach((team, idx) => {
        const zIdx = idx % numZones;
        zones[zIdx].teams.push({ teamId: team.id, name: team.name, pts: 0, pj: 0, pg: 0, sf: 0, sc: 0 });
      });

      await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), {
        status: hasPlayoffs ? 'Fase de Grupos' : 'En Juego',
        lobbyStatus: 'closed',
        zones,
        hasPlayoffs,
        qualifiersPerZone: Number(qualifiersPerZone),
        startedAt: officialStartDate,
        advancedScheduleRestrictions: schedulePlayMode === 'restricted',
        schedulePlayMode,
        scheduleDaysPerRound: Number(scheduleDaysPerRound),
        scheduleTimeUnit,
        advancedDatesAllowed: advancedDatesAllowed === 'Todos' ? 'Todos' : Number(advancedDatesAllowed)
      });

      const currentUser = auth.currentUser;
      const currentUserProfile = players.find(p => p.id === currentUser?.uid);
      const activeUserName = currentUserProfile?.name || currentUser?.displayName || currentUser?.email || 'Administrador';
      await logActivity(
        db,
        getCollectionPath,
        currentUser?.uid || 'admin',
        activeUserName,
        'admin',
        `Inicio de Torneo (Formato Liga) - Torneo: ${t.name} (${zones.length} zonas)`
      );

      // Generar Partidos Liga
      let allMatches: any[] = [];
      zones.forEach((z, zIdx) => {
        const zoneTeams = finalTeams.filter((_, idx) => idx % numZones === zIdx);
        // Helper inline para round robin
        let rrTeams = [...zoneTeams];
        // @ts-ignore
        if (rrTeams.length % 2 !== 0) rrTeams.push(null);
        const n = rrTeams.length;
        const rounds = n - 1;
        for (let r = 0; r < rounds; r++) {
          for (let i = 0; i < n / 2; i++) {
            const t1 = rrTeams[i];
            const t2 = rrTeams[n - 1 - i];
            if (t1 || t2) {
              const mId = doc(collection(db, getCollectionPath('matches'))).id;
              let team1Id = '';
              let team2Id = '';
              
              if (t1 && t2) {
                team1Id = t1.id;
                team2Id = t2.id;
              } else if (t1) {
                team1Id = t1.id;
                team2Id = 'BYE';
              } else if (t2) {
                team1Id = t2.id;
                team2Id = 'BYE';
              }

              let participantIds: string[] = [];
              if (t1) {
                if (t1.player1Id) participantIds.push(t1.player1Id);
                if (t1.player2Id) participantIds.push(t1.player2Id);
                if (t1.player3Id) participantIds.push(t1.player3Id);
              }
              if (t2) {
                if (t2.player1Id) participantIds.push(t2.player1Id);
                if (t2.player2Id) participantIds.push(t2.player2Id);
                if (t2.player3Id) participantIds.push(t2.player3Id);
              }

              allMatches.push({
                id: mId,
                round: `Fecha ${r + 1}`,
                roundNumber: r + 1,
                tournamentId: t.id,
                tournamentName: t.name,
                zoneName: z.name,
                zoneIndex: zIdx,
                team1Id,
                team2Id,
                participantIds,
                organizerId: t.organizerId,
                status: 'pending',
                s1: '', s2: '', s3: '', score: '',
                winnerId: null,
                createdAt: Date.now() + allMatches.length
              });
            }
          }
          rrTeams.splice(1, 0, rrTeams.pop()!);
        }

        if (doubleRound) {
          const roundsOffset = rounds;
          const idaMatches = [...allMatches.filter(m => m.zoneIndex === zIdx)];
          idaMatches.forEach(m => {
            const mId = doc(collection(db, getCollectionPath('matches'))).id;
            allMatches.push({
              ...m,
              id: mId,
              round: `Fecha ${m.roundNumber + roundsOffset} (vuelta)`,
              roundNumber: m.roundNumber + roundsOffset,
              team1Id: m.team2Id,
              team2Id: m.team1Id,
              participantIds: m.participantIds,
              createdAt: Date.now() + allMatches.length
            });
          });
        }
      });

      // Batch matches creation (limit 500 per batch)
      const batchSize = 500;
      for (let i = 0; i < allMatches.length; i += batchSize) {
        const batch = writeBatch(db);
        const chunk = allMatches.slice(i, i + batchSize);
        for (const m of chunk) {
          batch.set(doc(db, getCollectionPath('matches'), m.id), m);
        }
        await batch.commit();
      }
    } else {
      // Eliminación (Lógica Brackets con términos locales)
      const n = finalTeams.length;
      let pow2 = 1; while (pow2 < n) pow2 *= 2;
      const brackets = [];
      let teamsAtThisRound = pow2;

      let currentTeams: any[] = [...finalTeams];

      if (!forcedTeams) {
        // En modo auto completamos con BYEs si falta para pot de 2
        for (let i = 0; i < pow2 - n; i++) currentTeams.push({ id: 'BYE', name: 'PASE DIRECTO' });
        if (placementMode === 'auto') {
          currentTeams.sort(() => Math.random() - 0.5);
        }
      }

      while (teamsAtThisRound >= 2) {
        const isFirst = teamsAtThisRound === pow2;
        const matchesInRound = teamsAtThisRound / 2;
        const matches = [];
        for (let i = 0; i < matchesInRound; i++) {
          const teamA = isFirst ? currentTeams[i * 2] : null;
          const teamB = isFirst ? currentTeams[i * 2 + 1] : null;
          matches.push({
            team1Id: teamA?.id || '',
            team2Id: teamB?.id || '',
            t1: teamA?.name || 'A DEFINIR',
            t2: teamB?.name || 'A DEFINIR',
            s1: '', s2: '', s3: '', score: '', winnerId: ''
          });
        }
        brackets.push({
          round: teamsAtThisRound === 2 ? 'Final' :
            teamsAtThisRound === 4 ? 'Semifinal' :
              teamsAtThisRound === 8 ? 'Cuartos' : 'Ronda de ' + teamsAtThisRound,
          matches
        });
        teamsAtThisRound /= 2;
      }

      await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), {
        status: 'Eliminatorias',
        lobbyStatus: 'closed',
        brackets,
        startedAt: officialStartDate,
        advancedScheduleRestrictions: schedulePlayMode === 'restricted',
        schedulePlayMode,
        scheduleDaysPerRound: Number(scheduleDaysPerRound),
        scheduleTimeUnit,
        advancedDatesAllowed: advancedDatesAllowed === 'Todos' ? 'Todos' : Number(advancedDatesAllowed)
      });

      const currentUser = auth.currentUser;
      const currentUserProfile = players.find(p => p.id === currentUser?.uid);
      const activeUserName = currentUserProfile?.name || currentUser?.displayName || currentUser?.email || 'Administrador';
      await logActivity(
        db,
        getCollectionPath,
        currentUser?.uid || 'admin',
        activeUserName,
        'admin',
        `Inicio de Torneo (Formato Eliminatorio) - Torneo: ${t.name} (${brackets.length} rondas)`
      );
    }

    // Notify all participants
    try {
      const participants = new Set<string>();
      sortedRegisteredTeams.forEach(tm => {
        if (tm.player1Id) participants.add(tm.player1Id);
        if (tm.player2Id) participants.add(tm.player2Id);
        if (tm.player3Id) participants.add(tm.player3Id);
      });

      const notifsBatch = writeBatch(db);
      participants.forEach(pid => {
        if (pid) {
          const notifRef = doc(collection(db, 'notifications'));
          notifsBatch.set(notifRef, {
            userId: pid,
            type: 'tournament_started',
            title: `¡Torneo Iniciado!`,
            body: `El torneo ${t.name} ha comenzado. ¡Mira tu nueva grilla de partidos actualizada!`,
            matchId: t.id,
            read: false,
            createdAt: Date.now()
          });
        }
      });
      await notifsBatch.commit();
    } catch (error) {
      console.error("Error sending tournament start notifications:", error);
    }

    setIsStarting(false);
    setIsPlacementOpen(false);

    // Redirect to "Torneos Activos" tab and keep this tournament expanded
    console.log('[AdminTournamentLobby] Transitioning to activos for:', t.id);
    window.dispatchEvent(new CustomEvent('changeAdminTab', {
      detail: { tab: 'activos', tournamentId: t.id }
    }));
  };

  const n = sortedRegisteredTeams.length;
  let suggestedPow2 = 1;
  if (n > 0) {
    while (suggestedPow2 < n) suggestedPow2 *= 2;
  }
  const pasesNeeded = n > 0 ? (suggestedPow2 - n) : 0;

  const handleStartProcedure = () => {
    console.log('Abriendo resumen de inicio...');
    setShowStartConfirmation(true);
    setTimeout(() => {
      lobbyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const confirmStartTournament = () => {
    setShowStartConfirmation(false);
    if (placementMode === 'manual' && t.type === 'torneo_eliminacion') {
      setIsPlacementOpen(true);
    } else {
      startTournament();
    }
  };

  const ManualPlacementUI = () => {
    const n = sortedRegisteredTeams.length;
    let pow2 = 1; while (pow2 < n) pow2 *= 2;
    const slotsCount = pow2;
    const assignedIds = Object.values(manualPlacement);
    const unassignedTeams = sortedRegisteredTeams.filter(tm => !assignedIds.includes(tm.id));

    // Dividir slots en dos lados (Izquierda / Derecha)
    const midPoint = slotsCount / 2;

    const handleConfirmManual = () => {
      const fullList = Array.from({ length: slotsCount }).map((_, i) => manualPlacement[`slot_${i}`] || 'BYE');
      startTournament(fullList);
    };

    const SlotItem = ({ index }: { index: number }) => {
      const teamId = manualPlacement[`slot_${index}`];
      const team = allTeams.find(tm => tm.id === teamId);
      return (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            const droppedId = e.dataTransfer.getData('teamId');
            if (droppedId) setManualPlacement({ ...manualPlacement, [`slot_${index}`]: droppedId });
          }}
          className={`relative p-3 rounded-xl border-2 transition-all flex items-center justify-between gap-3 h-14 ${team ? 'theme-primary-border bg-white/5' : 'border-dashed border-white/5 bg-black/20 hover:border-white/10'}`}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center border border-white/10 shrink-0 text-[10px] font-black opacity-40">
              {index + 1}
            </div>
            {team ? (
              <span className="font-black uppercase text-[10px] truncate">{team.name}</span>
            ) : (
              <span className="text-[9px] font-black uppercase opacity-20 tracking-widest italic">Vacío / Pase</span>
            )}
          </div>
          {team && (
            <button
              onClick={() => {
                const newMP = { ...manualPlacement };
                delete newMP[`slot_${index}`];
                setManualPlacement(newMP);
              }}
              className="p-1 text-white/20 hover:text-red-400 transition-colors"
            >
              <X size={12} />
            </button>
          )}
        </div>
      );
    };

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/95 backdrop-blur-2xl" onClick={() => setIsPlacementOpen(false)} />
        <div className="relative w-full max-w-6xl bg-slate-900 border border-white/10 rounded-[40px] shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
          <div className="p-8 border-b border-white/5 flex justify-between items-center bg-black/40">
            <div>
              <h4 className="text-2xl font-black uppercase italic theme-primary-text flex items-center gap-3">
                <Settings className="animate-spin-slow" />
                Posicionamiento Táctico
              </h4>
              <p className="text-[10px] font-bold opacity-40 uppercase tracking-widest mt-1">
                Llave simétrica de {pow2} parejas • Arrastra para definir los duelos iniciales
              </p>
            </div>
            <button onClick={() => setIsPlacementOpen(false)} className="p-3 bg-white/5 rounded-full hover:bg-white/10 transition-colors">
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-auto p-4 lg:p-8 custom-scrollbar grid grid-cols-1 lg:grid-cols-12 gap-8 bg-[url('https://picsum.photos/seed/tournament/1920/1080?blur=10')] bg-cover bg-center">
            <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm z-0" />

            {/* Unassigned Teams */}
            <div className="lg:col-span-3 space-y-4 relative z-10 bg-black/40 p-6 rounded-[var(--radius-large)] border border-white/5 h-fit max-h-full overflow-y-auto">
              <span className="text-[10px] font-black uppercase opacity-40 tracking-widest block mb-4 border-b border-white/5 pb-2">Parejas Disponibles ({unassignedTeams.length})</span>
              <div className="space-y-2">
                {unassignedTeams.map(tm => (
                  <div
                    key={tm.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('teamId', tm.id)}
                    onClick={() => {
                      const firstEmpty = Array.from({ length: slotsCount }).findIndex((_, i) => !manualPlacement[`slot_${i}`]);
                      if (firstEmpty !== -1) setManualPlacement({ ...manualPlacement, [`slot_${firstEmpty}`]: tm.id });
                    }}
                    className="bg-slate-800/80 border border-white/10 p-3 rounded-xl flex items-center gap-3 cursor-pointer hover:theme-primary-border hover:bg-slate-700 transition-all group active:scale-95"
                  >
                    <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center border border-white/10 shrink-0">
                      <Users size={16} className="opacity-20" />
                    </div>
                    <span className="font-black uppercase text-[10px] truncate">{tm.name}</span>
                  </div>
                ))}
                {unassignedTeams.length === 0 && (
                  <p className="text-[9px] font-black uppercase opacity-20 text-center py-4">Todas las parejas ubicadas</p>
                )}
              </div>
            </div>

            {/* Bracket Layout */}
            <div className="lg:col-span-9 relative z-10 flex flex-col md:flex-row gap-4 h-full">
              {/* Left Side */}
              <div className="flex-1 space-y-8">
                <span className="text-[9px] font-black uppercase opacity-40 tracking-[0.2em] block text-center">BLOQUE IZQUIERDO</span>
                <div className="space-y-12">
                  {Array.from({ length: midPoint / 2 }).map((_, matchIdx) => (
                    <div key={`left_${matchIdx}`} className="relative space-y-1">
                      <SlotItem index={matchIdx * 2} />
                      <div className="flex items-center justify-center h-4 text-[9px] font-black opacity-20">VS</div>
                      <SlotItem index={matchIdx * 2 + 1} />
                      {/* Connector to next round */}
                      <div className="absolute top-1/2 -right-4 w-4 h-[1px] bg-white/10" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Center Path Indicator */}
              <div className="hidden md:flex flex-col items-center justify-center px-4 opacity-10">
                <div className="w-[1px] h-full bg-gradient-to-b from-transparent via-white to-transparent" />
                <Trophy size={24} className="my-4" />
                <div className="w-[1px] h-full bg-gradient-to-b from-transparent via-white to-transparent" />
              </div>

              {/* Right Side */}
              <div className="flex-1 space-y-8">
                <span className="text-[9px] font-black uppercase opacity-40 tracking-[0.2em] block text-center">BLOQUE DERECHO</span>
                <div className="space-y-12">
                  {Array.from({ length: midPoint / 2 }).map((_, matchIdx) => {
                    const actualIdx = midPoint + (matchIdx * 2);
                    return (
                      <div key={`right_${matchIdx}`} className="relative space-y-1">
                        {/* Connector from next round */}
                        <div className="absolute top-1/2 -left-4 w-4 h-[1px] bg-white/10" />
                        <SlotItem index={actualIdx} />
                        <div className="flex items-center justify-center h-4 text-[9px] font-black opacity-20">VS</div>
                        <SlotItem index={actualIdx + 1} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 bg-black/60 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-6 relative z-20">
            <div className="flex items-center gap-4 text-xs font-bold uppercase opacity-60">
              <AlertCircle className={unassignedTeams.length > 0 ? 'text-amber-500' : 'theme-primary-text'} />
              <span>
                {unassignedTeams.length > 0
                  ? `Faltan ${unassignedTeams.length} parejas por ubicar`
                  : 'Todas las parejas están en posición táctica'}
              </span>
            </div>
            <div className="flex items-center gap-4 w-full md:w-auto">
              <button
                onClick={() => setManualPlacement({})}
                className="px-8 py-4 bg-white/5 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-white/10 transition-colors"
              >
                Resetear
              </button>
              <button
                onClick={handleConfirmManual}
                disabled={isStarting}
                className={`flex-1 md:flex-none px-12 py-4 rounded-2xl font-black uppercase text-sm tracking-widest shadow-xl transition-all ${unassignedTeams.length > 0 ? 'bg-white/10 opacity-50 cursor-not-allowed' : 'theme-primary-bg text-black hover:scale-105 active:scale-95'}`}
              >
                {isStarting ? 'Procesando...' : 'Confirmar Sembrado'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const allApproved = sortedRegisteredTeams.length > 0 &&
    sortedRegisteredTeams.every(team => t.approvedTeamIds?.includes(team.id));

  return (
    <div ref={lobbyRef} className="space-y-5 animate-in fade-in duration-500 relative scroll-mt-20">
      {isPlacementOpen && <ManualPlacementUI />}
      {errorModal.show && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setErrorModal({ show: false, message: '' })} />
          <div className="relative w-full max-w-xs bg-slate-900 border border-amber-500/30 rounded-2xl p-6 shadow-2xl flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-500 mb-4">
              <Info size={24} />
            </div>
            <p className="font-black text-white uppercase text-xs mb-6">{errorModal.message}</p>
            <button
              onClick={() => setErrorModal({ show: false, message: '' })}
              className="w-full bg-white/10 hover:bg-white/20 py-3 rounded-xl uppercase font-black text-[10px] tracking-widest transition-all"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {isEditingInfoProp === undefined && (
        <div className="flex justify-center pt-1 min-h-[40px]">
          <AnimatePresence mode="wait">
            {!showExitConfirm ? (
              <motion.button
                key="main-btn"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                onClick={() => {
                  if (isEditingInfo) {
                    if (hasChanges) {
                      setShowExitConfirm(true);
                    } else {
                      setIsEditingInfo(false);
                    }
                  } else {
                    setIsEditingInfo(true);
                  }
                }}
                className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all border shadow-lg ${isEditingInfo ? 'theme-primary-bg text-black border-theme-primary' : 'bg-white/5 hover:bg-white/10 text-white/50 hover:text-white border-white/10'}`}
              >
                {isEditingInfo ? <X size={14} /> : <Edit size={14} />}
                {isEditingInfo ? 'Cerrar Edición' : 'Editar Información del Torneo'}
              </motion.button>
            ) : (
              <motion.div
                key="confirm-area"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="flex items-center gap-4 bg-slate-900 border border-amber-500/30 p-2 pl-5 rounded-2xl shadow-2xl"
              >
                <div className="flex flex-col">
                  <span className="text-[9px] font-black uppercase theme-primary-text tracking-widest leading-none">¿Guardar cambios?</span>
                  <span className="text-[7px] font-bold opacity-30 uppercase tracking-tighter mt-0.5">Se perderán si cierras ahora</span>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <button
                    onClick={async () => {
                      await handleUpdateInfo();
                      setShowExitConfirm(false);
                    }}
                    className="px-4 py-2 theme-primary-bg text-black font-black rounded-xl uppercase text-[9px] tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-md"
                  >
                    Sí, Guardar
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingInfo(false);
                      setShowExitConfirm(false);
                      setEditData({
                        name: t.name,
                        type: t.type,
                        category: t.category || '5ta',
                        gender: t.gender || 'masculino',
                        maxTeams: t.maxTeams || 16,
                        entryFee: t.entryFee || 0,
                        startDate: t.startDate || '',
                        description: t.description || '',
                        rulesText: t.rulesText || '',
                        rewards: t.rewards || [],
                        locationMode: t.locationMode || 'tbd',
                        locationCity: t.locationCity || '',
                        locationClubIds: t.locationClubIds || [],
                      });
                    }}
                    className="px-4 py-2 bg-red-500/10 text-red-500 font-black rounded-xl uppercase text-[9px] tracking-widest hover:bg-red-500/20 active:scale-95 transition-all"
                  >
                    No, Descartar
                  </button>
                  <button
                    onClick={() => setShowExitConfirm(false)}
                    className="p-2 text-white/20 hover:text-white transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {isEditingInfo && createPortal(
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsEditingInfo(false)} />
          <div className="relative w-full max-w-2xl bg-slate-900 shadow-2xl rounded-[var(--radius-large)] border border-white/10 p-5 md:p-6 animate-in zoom-in-95 duration-200 overflow-y-auto max-h-[90vh] custom-scrollbar">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 1 - Nombre del torneo */}
            <div className="space-y-1 md:col-span-2">
              <label className="text-[9px] font-black uppercase opacity-40 ml-1">Nombre del Torneo</label>
              <input
                type="text"
                value={editData.name}
                onChange={e => setEditData({ ...editData, name: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg p-2 text-xs font-bold outline-none focus:theme-primary-border selection:bg-lime-500/30"
              />
            </div>

            {/* 2 - Descripción */}
            <div className="space-y-1 md:col-span-2">
              <label className="text-[9px] font-black uppercase opacity-40 ml-1">Descripción</label>
              <textarea
                value={editData.description}
                placeholder="Breve descripción del evento..."
                onChange={e => {
                  setEditData({ ...editData, description: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                ref={el => {
                  if (el) {
                    el.style.height = 'auto';
                    el.style.height = `${el.scrollHeight}px`;
                  }
                }}
                rows={1}
                className="w-full bg-white/5 border border-white/10 rounded-lg p-2 text-xs font-bold outline-none focus:theme-primary-border selection:bg-lime-500/30 resize-none overflow-hidden min-h-[36px]"
              />
            </div>

            {/* Ubicación */}
            <div className="space-y-2 md:col-span-2 pt-3 border-t border-white/5">
              <label className="text-[9px] font-black uppercase opacity-40 ml-1 block w-full">Dónde se juega</label>
              
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => setEditData({ ...editData, locationMode: 'clubs' })}
                  className={`flex-1 py-1.5 rounded-lg font-black uppercase tracking-widest text-[9px] border transition-all ${editData.locationMode === 'clubs' ? 'theme-primary-border theme-primary-text bg-theme-primary/10 shadow-lg' : 'border-white/5 bg-black/20 opacity-40 hover:opacity-100'}`}
                >
                  Elegir Club(es)
                </button>
                <button
                  onClick={() => setEditData({ ...editData, locationMode: 'tbd', locationClubIds: [] })}
                  className={`flex-1 py-1.5 rounded-lg font-black uppercase tracking-widest text-[9px] border transition-all ${editData.locationMode === 'tbd' ? 'theme-primary-border theme-primary-text bg-theme-primary/10 shadow-lg' : 'border-white/5 bg-black/20 opacity-40 hover:opacity-100'}`}
                >
                  A determinar
                </button>
              </div>

              {editData.locationMode === 'clubs' ? (
                <div className="space-y-2">
                  <div className="text-[9px] font-bold opacity-50 uppercase tracking-widest text-center">
                    Selecciona hasta 2 clubes sede ({editData.locationClubIds.length}/2)
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[100px] overflow-y-auto custom-scrollbar pr-1">
                    {sedesPublicas?.map(sede => (
                      <button
                        key={sede.uid}
                        onClick={() => {
                          const clubIds = [...editData.locationClubIds];
                          if (clubIds.includes(sede.uid)) {
                            setEditData({ ...editData, locationClubIds: clubIds.filter(id => id !== sede.uid) });
                          } else {
                            if (clubIds.length < 2) {
                              setEditData({ ...editData, locationClubIds: [...clubIds, sede.uid] });
                            } else {
                              alert('Solo puedes seleccionar hasta 2 clubes.');
                            }
                          }
                        }}
                        className={`flex items-center gap-2 p-1.5 rounded-lg border transition-all text-left ${
                          editData.locationClubIds.includes(sede.uid)
                            ? 'theme-primary-border bg-theme-primary/10'
                            : 'border-white/5 bg-black/20 hover:border-white/20'
                        }`}
                      >
                        <div className="w-6 h-6 rounded bg-black/40 overflow-hidden flex-shrink-0">
                          {sede.photoUrl ? (
                            <img src={sede.photoUrl} alt={sede.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[8px] text-white/20">S/F</div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] font-bold text-white truncate">{sede.name}</div>
                          <div className="text-[8px] opacity-50 truncate">{sede.city}</div>
                        </div>
                      </button>
                    ))}
                    {(!sedesPublicas || sedesPublicas.length === 0) && (
                      <div className="col-span-2 text-center text-xs opacity-50 py-2">No hay clubes disponibles.</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-[9px] font-black uppercase tracking-widest opacity-50 block text-center">
                    Ciudad de realización
                  </label>
                  <select
                    value={editData.locationCity}
                    onChange={(e) => setEditData({ ...editData, locationCity: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[10px] uppercase font-bold outline-none focus:theme-primary-border text-white appearance-none"
                  >
                    <option value="" disabled>Seleccionar Ciudad...</option>
                    {ALL_CITIES.map(c => (
                      <option key={c.id} value={c.id} className="bg-slate-900">{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Contenedor de 3 columnas para campos cortos */}
            <div className="grid grid-cols-3 gap-3 md:col-span-2 pt-3 border-t border-white/5">
              {/* 3 - Formato de torneo */}
              <div className="space-y-1 col-span-1">
                <label className="text-[8px] font-black uppercase opacity-40 text-center block w-full">Formato</label>
                <select
                  value={editData.type}
                  onChange={e => setEditData({ ...editData, type: e.target.value as any })}
                  className="w-full bg-white/5 text-white border border-white/10 rounded-lg p-2 font-bold outline-none focus:theme-primary-border uppercase text-[10px] text-center [&>option]:bg-slate-900 [&>option]:text-white"
                >
                  <option value="liga" className="bg-slate-900 text-white text-center">Zonas</option>
                  <option value="torneo_eliminacion" className="bg-slate-900 text-white text-center">Directo</option>
                </select>
              </div>

              {/* 4 - Categoría */}
              <div className={`space-y-1 ${editData.category?.startsWith('Suma') ? 'col-span-2' : 'col-span-1'}`}>
                <label className="text-[8px] font-black uppercase opacity-40 text-center block w-full">Categoría</label>
                <div className="flex gap-2">
                  <select
                    value={editData.category?.startsWith('Suma') ? 'Suma' : editData.category}
                    onChange={e => {
                      if (e.target.value === 'Suma') {
                        setEditData({ ...editData, category: 'Suma 13' });
                      } else {
                        setEditData({ ...editData, category: e.target.value });
                      }
                    }}
                    className="flex-1 w-full bg-white/5 text-white border border-white/10 rounded-lg p-2 font-bold outline-none focus:theme-primary-border uppercase text-[10px] text-center [&>option]:bg-slate-900 [&>option]:text-white"
                  >
                    {['Libre', '1era', '2da', '3ra', '4ta', '5ta', '6ta', '7ma', '8va', 'Suma'].map(cat => (
                      <option key={cat} value={cat} className="bg-slate-900 text-white text-center">{cat}</option>
                    ))}
                  </select>
                  {editData.category?.startsWith('Suma') && (
                    <input 
                      type="number"
                      min="2" max="16"
                      value={editData.category.replace('Suma ', '')}
                      onChange={e => setEditData({ ...editData, category: `Suma ${e.target.value}` })}
                      className="w-14 bg-white/5 text-white border border-white/10 rounded-lg p-2 font-black outline-none focus:theme-primary-border text-[10px] text-center"
                    />
                  )}
                </div>
              </div>

              {/* 5 - Género */}
              <div className="space-y-1 col-span-1">
                <label className="text-[8px] font-black uppercase opacity-40 text-center block w-full">Género</label>
                <select
                  value={editData.gender}
                  onChange={e => setEditData({ ...editData, gender: e.target.value as any })}
                  className="w-full bg-white/5 text-white border border-white/10 rounded-lg p-2 font-bold outline-none focus:theme-primary-border uppercase text-[10px] text-center [&>option]:bg-slate-900 [&>option]:text-white"
                >
                  <option value="Sin Restricción" className="bg-slate-900 text-white text-center">Libre</option>
                  <option value="masculino" className="bg-slate-900 text-white text-center">Masc.</option>
                  <option value="femenino" className="bg-slate-900 text-white text-center">Fem.</option>
                  <option value="mixto" className="bg-slate-900 text-white text-center">Mixto</option>
                </select>
              </div>

              {/* 6 - Fecha estimada */}
              <div className="space-y-1 col-span-1">
                <label className="text-[8px] font-black uppercase opacity-40 text-center block w-full">Fecha Estimada</label>
                <div className="relative group overflow-hidden rounded-lg">
                  <input
                    type="date"
                    value={editData.startDate}
                    onChange={e => setEditData({ ...editData, startDate: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-2 font-bold outline-none focus:theme-primary-border relative z-10 cursor-pointer appearance-none selection:bg-theme-primary/30 text-center text-[10px] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 z-0 pointer-events-none">
                    <CalendarDays size={12} className="theme-primary-text opacity-40 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </div>

              {/* 7 - Valor de inscripción */}
              <div className="space-y-1 col-span-1">
                <label className="text-[8px] font-black uppercase opacity-40 text-center block w-full">Costo ($)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={editData.entryFee === 0 ? '' : editData.entryFee}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setEditData({ ...editData, entryFee: val === '' ? 0 : parseInt(val) });
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-2 font-bold outline-none focus:theme-primary-border selection:bg-lime-500/30 text-[10px] text-center"
                />
              </div>

              {/* 8 - Cupos */}
              <div className="space-y-1 col-span-1">
                <label className="text-[8px] font-black uppercase opacity-40 text-center block w-full">Cupos</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={editData.maxTeams === 0 ? '' : editData.maxTeams}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setEditData({ ...editData, maxTeams: val === '' ? 0 : parseInt(val) });
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-2 font-bold outline-none focus:theme-primary-border selection:bg-lime-500/30 text-[10px] text-center"
                />
              </div>
            </div>

            {/* 9 - Reglamento (link) */}
            <div className="space-y-1 md:col-span-2 pt-3 border-t border-white/5">
              <label className="text-[9px] font-black uppercase opacity-40 ml-1">Reglamento (Link a PDF/Drive)</label>
              <input
                type="text"
                value={editData.rulesText}
                placeholder="https://..."
                onChange={e => setEditData({ ...editData, rulesText: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg p-2 font-bold outline-none focus:theme-primary-border text-xs selection:bg-lime-500/30"
              />
            </div>

            <div className="md:col-span-2 space-y-3 pt-3 border-t border-white/5">
              <div className="flex justify-between items-center">
                <label className="text-[9px] font-black uppercase opacity-40 ml-1">Premios</label>
                <button
                  onClick={() => {
                    const defaultRanks = ['1er Puesto', '2do Puesto', '3er Puesto', 'Semifinalistas', '4tos de Final', 'Octavos de Final'];
                    const nextRank = defaultRanks[editData.rewards.length] || '';
                    setEditData({ ...editData, rewards: [...editData.rewards, { rank: nextRank, prize: '' }] });
                  }}
                  className="flex items-center gap-1.5 text-[9px] font-black uppercase theme-primary-text hover:opacity-80 transition-opacity"
                >
                  <Plus size={10} /> Agregar Premio
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {editData.rewards.map((r, i) => (
                  <div key={i} className="flex gap-2 items-center bg-white/5 p-1.5 rounded-lg border border-white/5">
                    <div className="flex-1 space-y-0.5">
                      <input
                        placeholder="Ej: 1er Puesto"
                        value={r.rank}
                        onChange={e => {
                          const newList = [...editData.rewards];
                          newList[i].rank = e.target.value;
                          setEditData({ ...editData, rewards: newList });
                        }}
                        className="w-full bg-transparent border-none p-1 text-[9px] font-black uppercase outline-none theme-primary-text selection:bg-theme-primary/30"
                      />
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <input
                            placeholder="Ej: $100.000 + Copa"
                            value={r.prize}
                            onChange={e => {
                              const newList = [...editData.rewards];
                              newList[i].prize = e.target.value;
                              setEditData({ ...editData, rewards: newList });
                            }}
                            className="w-full bg-transparent border-none p-1 text-[10px] font-bold outline-none selection:bg-theme-primary/30"
                          />
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setEditData({ ...editData, rewards: editData.rewards.filter((_, idx) => idx !== i) })}
                      className="p-2 text-red-500/40 hover:text-red-500 transition-colors"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 md:col-span-2 pt-4">
              <button
                onClick={() => setIsEditingInfo(false)}
                className="w-full bg-white/5 hover:bg-white/10 text-white font-black py-3 rounded-2xl uppercase tracking-widest text-xs transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  handleUpdateInfo();
                  setIsEditingInfo(false);
                }}
                className="w-full theme-primary-bg text-black font-black py-3 rounded-2xl uppercase tracking-widest text-xs shadow-xl hover:brightness-110 transition-all"
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
        </div>,
        document.body
      )}

      {/* Lobby Management */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Pending Requests Section */}
        {(() => {
          const pendingCount = (t.pendingTeamIds || []).length;
          return (
            <div className={`lg:col-span-12 rounded-2xl transition-all duration-700 bg-slate-500/5 border border-slate-500/20 p-4 space-y-3 shadow-inner ${pendingCount === 0 ? 'opacity-80 hover:opacity-100' : ''}`}>
              <div className={`flex flex-col gap-0.5`}>
                <h4 className="font-black uppercase italic flex items-center gap-2 text-slate-400 transition-all duration-500 text-[10px] tracking-normal">
                  <UserCheck size={14} />
                  Parejas Pendientes (Pre-inscriptas) ({pendingCount})
                </h4>
                {pendingCount > 0 && (
                  <p className="text-[8px] font-bold text-slate-500 uppercase tracking-tight ml-5 animate-in fade-in slide-in-from-left-1">Decide si cumple con los requisitos para participar</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(220px,320px))] gap-3 animate-in fade-in zoom-in-95 duration-500">
                {(t.pendingTeamIds || []).map(tid => {
                  const team = allTeams.find(tm => tm.id === tid);
                  if (!team) {
                    return (
                      <div key={tid} className="bg-black/40 border border-slate-500/20 p-1.5 rounded-xl flex items-center justify-between group hover:border-slate-500/30 transition-all opacity-60 min-w-[180px] max-w-[280px]">
                        <div className="flex items-center gap-2 truncate">
                          <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                            <UserCheck size={14} className="text-amber-500/50" />
                          </div>
                          <div className="truncate">
                            <p className="font-black uppercase text-[9px] truncate leading-tight opacity-60">Equipo pendiente</p>
                            <p className="font-bold text-[8px] truncate leading-tight opacity-40">ID: {tid.slice(0, 8)}...</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => handleRejectRegistration(tid)}
                            className="p-1 bg-red-400/20 text-red-300 rounded-lg hover:bg-red-400/30 transition-all"
                            title="Rechazar"
                          >
                            <XCircle size={12} />
                          </button>
                          <button
                            onClick={() => handleApproveRegistration(tid)}
                            className="p-1 bg-lime-400/20 text-lime-300 rounded-lg hover:bg-lime-400/30 transition-all shadow-md"
                            title="Aceptar"
                          >
                            <CheckCircle2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  }
                  const p1 = players.find(p => p.id === team.player1Id);
                  const p2 = players.find(p => p.id === team.player2Id);
                  const teamParts = team.name?.split(' & ') || [];
                  const p1Name = p1?.name || teamParts[0] || 'Jugador 1';
                  const p2Name = p2?.name || teamParts[1] || 'Jugador 2';

                  return (
                    <div key={tid} className="bg-white/[0.03] backdrop-blur-md border border-white/5 p-2 rounded-xl flex items-center justify-between group hover:bg-white/[0.06] hover:border-white/10 transition-all duration-300 min-w-[180px] max-w-[280px]">
                      <div className="flex items-center gap-2.5">
                        <PairAvatar
                          p1Id={team.player1Id}
                          p2Id={team.player2Id}
                          p1Image={p1?.photoUrl}
                          p2Image={p2?.photoUrl}
                          className="w-9 h-9 shrink-0"
                          onPlayerClick={(id) => {
                            const p = players.find(player => player.id === id);
                            if (p) setSelectedPlayer(p);
                          }}
                        />
                        <div className="truncate">
                          <p className="font-black uppercase text-[10px] truncate leading-tight tracking-tight">{p1Name}</p>
                          <p className="font-black uppercase text-[10px] truncate leading-tight mt-0.5 tracking-tight">{p2Name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleRejectRegistration(tid)}
                          className="p-1.5 bg-red-400/20 text-red-300 rounded-lg hover:bg-red-400/30 transition-all"
                          title="Rechazar"
                        >
                          <XCircle size={14} />
                        </button>
                        <button
                          onClick={() => handleApproveRegistration(tid)}
                          className="p-1.5 bg-lime-400/20 text-lime-300 rounded-lg hover:bg-lime-400/30 transition-all shadow-md"
                          title="Aceptar"
                        >
                          <CheckCircle2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {/* Empty Slots */}
                {Array.from({ length: Math.max(0, 1 - pendingCount) }).map((_, idx) => (
                  <div key={`empty-pending-${idx}`} className="bg-white/[0.01] backdrop-blur-md border border-dashed border-white/5 p-2 rounded-xl flex items-center justify-between opacity-30 select-none pointer-events-none min-w-[180px] max-w-[280px]">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                        <UserCheck size={14} className="text-white/20" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <div className="w-20 h-2 bg-white/10 rounded-full"></div>
                        <div className="w-14 h-1.5 bg-white/5 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Teams List */}
        <div className="lg:col-span-8 space-y-4">
          <div className="flex flex-col xl:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              {/* Inscribir Pareja Button */}
              <button
                onClick={() => setShowCoupleBuilder(true)}
                disabled={isFull}
                className="group/btn relative px-4 h-9 flex items-center justify-center theme-primary-bg text-black rounded-xl transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-20 overflow-hidden shadow-xl"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300" />
                <div className="flex items-center gap-1.5 relative z-10">
                  <Plus size={14} />
                  <span className="font-black uppercase text-[9px] tracking-widest">Inscribir pareja</span>
                </div>
              </button>

              {/* Agregar Invitado Button */}
              <button
                onClick={() => setShowAddGuest(true)}
                className="px-4 h-9 flex items-center justify-center bg-white/[0.03] hover:bg-white/[0.08] text-white rounded-xl transition-all border border-white/5 shadow-xl hover:border-white/10 active:scale-95"
              >
                <div className="flex items-center gap-1.5">
                  <Users size={12} className="opacity-40" />
                  <span className="font-black uppercase text-[9px] tracking-widest">Agregar Invitado</span>
                  <span className="text-[7px] font-bold text-white/30 uppercase tracking-widest mt-0.5 ml-1 hidden 2xl:inline">sin registrar</span>
                </div>
              </button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3">
              <h4 className="text-sm font-black uppercase italic flex items-center gap-2 opacity-80 shrink-0">
                <Users size={16} className="theme-primary-text" />
                Inscriptos <span className="text-[10px] opacity-60 ml-1 font-bold"><span className="text-[12px]">{t.approvedTeamIds?.length || 0}/{t.maxTeams || 16}</span> Aprobados</span>
              </h4>
              {sortedRegisteredTeams.length > 0 && (
                <button
                  onClick={approveAllTeams}
                  className={`text-[9px] h-9 px-4 rounded-xl font-black uppercase transition-all border ${(t.registeredTeamIds || []).length > 0 && (t.registeredTeamIds || []).every(id => t.approvedTeamIds?.includes(id))
                    ? 'bg-white text-black border-white hover:bg-white/90 shadow-[0_0_15px_rgba(255,255,255,0.3)]'
                    : 'bg-lime-500/10 text-lime-400 border-lime-500/30 hover:bg-lime-500/20 hover:text-lime-300 hover:border-lime-500/50 shadow-[0_0_10px_rgba(163,230,53,0.1)]'
                    }`}
                >
                  {(t.registeredTeamIds || []).length > 0 && (t.registeredTeamIds || []).every(id => t.approvedTeamIds?.includes(id))
                    ? 'Desmarcar Todos'
                    : 'Aprobar Todos'}
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sortedRegisteredTeams.map((team, idx) => {
              const isApproved = t.approvedTeamIds?.includes(team.id);
              return (
                <div key={team.id} className={`backdrop-blur-md border px-4 py-3 rounded-2xl flex items-center justify-between group h-16 transition-all duration-300 ${isApproved ? 'border-lime-400/40 bg-lime-400/[0.08] shadow-[0_0_15px_rgba(163,230,53,0.1)]' : 'bg-white/[0.03] border-white/5 hover:bg-white/[0.06] hover:border-white/10'}`}>
                  <div className="flex items-center gap-3">
                    <PairAvatar
                      p1Id={team.player1Id}
                      p2Id={team.player2Id}
                      p1Image={players.find(p => p.id === team.player1Id)?.photoUrl}
                      p2Image={players.find(p => p.id === team.player2Id)?.photoUrl}
                      className="w-10 h-10"
                      onPlayerClick={(id) => {
                        const p = players.find(player => player.id === id);
                        if (p) setSelectedPlayer(p);
                      }}
                    />
                    <div className="truncate flex flex-col gap-0.5">
                      <p className="font-black uppercase text-[11px] truncate leading-tight tracking-tight">
                        {formatPairName(team.name, 'short')}
                      </p>
                      {isApproved && <span className="text-[8px] text-lime-400 font-black uppercase tracking-widest">Pago Verificado</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => toggleTeamApproval(team.id)}
                      className={`p-2 rounded-xl transition-all ${isApproved ? 'text-lime-400 bg-lime-400/10 hover:bg-lime-400/20' : 'text-white/20 hover:text-white/40 hover:bg-white/5'}`}
                      title={isApproved ? "Desaprobar" : "Aprobar pago/requisitos"}
                    >
                      <CheckCircle2 size={18} />
                    </button>
                    <button
                      onClick={() => removeTeamFromLobby(team.id)}
                      className="p-2 text-red-500/20 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )
            })}

            {/* Empty Slots */}
            {Array.from({ length: Math.max(0, (t.maxTeams || 16) - sortedRegisteredTeams.length) }).map((_, idx) => (
              <div key={`empty-${idx}`} className="backdrop-blur-md border px-4 py-3 rounded-2xl flex items-center justify-between h-16 bg-white/[0.01] border-dashed border-white/5 opacity-40 select-none pointer-events-none">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                    <Users size={16} className="text-white/20" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="w-24 h-2.5 bg-white/10 rounded-full"></div>
                    <div className="w-16 h-2 bg-white/5 rounded-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar Controls */}
        <div className="lg:col-span-4 space-y-4">
          <div className="bg-slate-900/40 backdrop-blur-xl border border-white/10 rounded-[32px] p-6 space-y-6 shadow-2xl relative group/sidebar">
            <div className="absolute inset-0 bg-gradient-to-br from-theme-primary/5 via-transparent to-transparent opacity-50 pointer-events-none rounded-[32px] overflow-hidden" />

            <div className="space-y-4 relative z-10">
              {/* Lista de Invitados Recientes */}
              {(() => {
                const availableGuests = players.filter(p => p.isGuest && p.guestTournamentId === t.id && !registeredTeams.flatMap(rt => [rt.player1Id, rt.player2Id]).includes(p.id));
                if (availableGuests.length > 0) {
                  return (
                    <div className="pt-2">
                      <h6 className="text-[8px] font-black uppercase tracking-widest text-white/40 mb-2 px-1">Invitados Disponibles</h6>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto no-scrollbar">
                        {availableGuests.map(guest => (
                          <div key={guest.id} className="flex items-center justify-between px-3 py-2 bg-white/[0.02] border border-white/5 rounded-xl">
                            <span className="text-[10px] font-bold uppercase truncate pr-2">{guest.name}</span>
                            <span className="text-[8px] font-black uppercase tracking-widest text-blue-400 bg-blue-400/10 px-2 py-0.5 rounded-md">Invitado</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Configuración Inicio Section */}
            <div className="space-y-6 relative z-0">
              <div className="flex items-center justify-center gap-1.5 px-1">
                <Settings size={12} className="text-white/20 shrink-0" />
                <h5 className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Configuración Inicio</h5>
              </div>

              {t.type === 'torneo_eliminacion' ? (
                <div className="space-y-4">
                  <div className="bg-white/[0.02] p-4 rounded-2xl border border-white/5 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-black uppercase theme-primary-text">Modo Sorteo</span>
                        <span className="text-[8px] font-bold opacity-30 uppercase tracking-tight">Estrategia de posicionamiento</span>
                      </div>
                      <div className="flex bg-white/[0.05] p-1 rounded-xl border border-white/5">
                        <button
                          onClick={() => setPlacementMode('auto')}
                          className={`px-4 py-2 rounded-lg text-[9px] font-black uppercase transition-all ${placementMode === 'auto' ? 'theme-primary-bg text-black shadow-lg' : 'opacity-30 hover:opacity-60'}`}
                        >Auto</button>
                        <button
                          onClick={() => setPlacementMode('manual')}
                          className={`px-4 py-2 rounded-lg text-[9px] font-black uppercase transition-all ${placementMode === 'manual' ? 'theme-primary-bg text-black shadow-lg' : 'opacity-30 hover:opacity-60'}`}
                        >Manual</button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 px-2 py-1">
                    <div className="w-1.5 h-1.5 rounded-full theme-primary-bg animate-pulse shrink-0" />
                    <p className="text-[9px] font-bold opacity-40 uppercase leading-tight italic">
                      {placementMode === 'auto'
                        ? 'Las parejas se mezclarán al azar al comenzar el torneo.'
                        : 'Podrás ubicar a cada pareja manualmente antes de generar la llave.'}
                    </p>
                  </div>

                  {registeredTeams.length > 1 && (
                    <div className="bg-amber-500/5 border border-amber-500/10 p-4 rounded-2xl flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0 border border-amber-500/20">
                        <LayoutList size={18} className="text-amber-500" />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-black uppercase text-amber-500/80">Estructura Brackets</span>
                        <p className="text-[9px] font-bold text-amber-500/60 uppercase leading-none">
                          Llave de {suggestedPow2} {pasesNeeded > 0 ? `• ${pasesNeeded} Pases` : ''}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 mt-4">
                  {/* Zonas y Clasificados */}
                  <div className="col-span-2 bg-white/[0.02] p-3 rounded-2xl border border-white/5 space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      {/* Cantidad Zonas */}
                      <div className="flex-1 space-y-2 text-center">
                        <label className="text-[8px] font-black opacity-40 uppercase flex items-center justify-center gap-1 w-full text-center">
                          <Hash size={10} /> Cantidad de Zonas
                          <div title="El posicionamiento en zonas será aleatorio" className="cursor-help text-white/30 hover:text-white/80 transition-colors">
                            <Info size={10} />
                          </div>
                        </label>
                        <div className="flex items-center justify-between bg-white/[0.03] p-1 rounded-xl border border-white/5 h-10">
                          <button
                            type="button"
                            onClick={() => setNumZones((prev) => { const arr = [1,2,3,4,5,6,8,12,16]; return arr[Math.max(0, arr.indexOf(prev) - 1)] || 1; })}
                            className="w-7 h-7 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-colors flex items-center justify-center text-white/60 hover:text-white"
                          >
                            <Minus size={10} />
                          </button>
                          <div className="flex flex-col items-center">
                            <span className="text-[11px] font-black text-white/90">{numZones}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setNumZones((prev) => { const arr = [1,2,3,4,5,6,8,12,16]; return arr[Math.min(arr.length - 1, arr.indexOf(prev) + 1)] || 16; })}
                            className="w-7 h-7 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-colors flex items-center justify-center text-white/60 hover:text-white"
                          >
                            <Plus size={10} />
                          </button>
                        </div>
                      </div>

                      {/* Clasif por zona */}
                      <div className="flex-1 space-y-2 text-center">
                        <label className="text-[8px] font-black opacity-40 uppercase flex items-center justify-center gap-1 w-full text-center">
                          <CheckCircle2 size={10} /> Clasifican por zona
                        </label>
                        <div className="flex items-center justify-between bg-white/[0.03] p-1 rounded-xl border border-white/5 h-10">
                          <button
                            type="button"
                            onClick={() => setQualifiersPerZone(Math.max(1, qualifiersPerZone - 1))}
                            className="w-7 h-7 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-colors flex items-center justify-center text-white/60 hover:text-white"
                          >
                            <Minus size={10} />
                          </button>
                          <div className="flex flex-col items-center">
                            <span className="text-[11px] font-black text-white/90">{qualifiersPerZone}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setQualifiersPerZone(Math.min(10, qualifiersPerZone + 1))}
                            className="w-7 h-7 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-colors flex items-center justify-center text-white/60 hover:text-white"
                          >
                            <Plus size={10} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Formato de Encuentros */}
                  <div className="bg-white/[0.02] p-3 rounded-2xl border border-white/5 space-y-2 text-center flex flex-col justify-between">
                    <label className="text-[8px] font-black opacity-40 uppercase flex items-center justify-center gap-1 w-full text-center">
                      <Repeat size={10} /> Formato
                    </label>
                    <div className="flex gap-1.5 bg-black/20 p-1 rounded-xl border border-amber-500/30 w-full mx-auto">
                      <button
                        onClick={() => setDoubleRound(false)}
                        className={`flex-1 py-1.5 rounded-lg text-[8px] font-black uppercase transition-all ${!doubleRound ? 'theme-primary-bg text-black shadow-lg' : 'opacity-40 hover:opacity-80 bg-white/5'}`}
                      >Ida</button>
                      <button
                        onClick={() => setDoubleRound(true)}
                        className={`flex-1 py-1.5 rounded-lg text-[8px] font-black uppercase transition-all ${doubleRound ? 'theme-primary-bg text-black shadow-lg' : 'opacity-40 hover:opacity-80 bg-white/5'}`}
                      >Ida y Vuelta</button>
                    </div>
                  </div>

                  {t.managementMode !== 'express' && (
                    <>
                      {/* Duración de Fecha (Frecuencia) */}
                      <div className="bg-white/[0.02] p-3 rounded-2xl border border-white/5 space-y-2 text-center flex flex-col justify-between">
                        <label className="text-[8px] font-black opacity-40 uppercase flex items-center justify-center gap-1 w-full text-center">
                          <CalendarDays size={10} /> Duración Fecha
                        </label>
                        <div className="flex items-center justify-between bg-white/[0.03] p-1 rounded-xl border border-white/5">
                          <button
                            type="button"
                            onClick={() => setScheduleDaysPerRound(Math.max(1, scheduleDaysPerRound - 1))}
                            className="w-7 h-7 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-colors flex items-center justify-center text-white/60 hover:text-white"
                          >
                            <Minus size={10} />
                          </button>
                          <div className="flex flex-col items-center">
                            <span className="text-[11px] font-black text-white/90">{scheduleDaysPerRound}</span>
                            <span className="text-[7px] font-bold opacity-30 uppercase">Días</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setScheduleDaysPerRound(scheduleDaysPerRound + 1)}
                            className="w-7 h-7 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-colors flex items-center justify-center text-white/60 hover:text-white"
                          >
                            <Plus size={10} />
                          </button>
                        </div>
                      </div>
                      {/* Adelanto de Fechas */}
                      <div className="col-span-2 bg-white/[0.02] p-3 rounded-2xl border border-white/5 space-y-3">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <label className="text-[10px] font-black opacity-60 uppercase flex items-center gap-1 w-full">
                              <CalendarDays size={12} /> Adelanto de Fechas
                            </label>
                            <p className="text-[7.5px] font-bold text-white/40 mt-0.5 leading-tight">
                              Permite jugar partidos de fechas futuras
                            </p>
                          </div>
                          <div className="flex items-center justify-between bg-white/[0.03] p-1 rounded-xl border border-white/5 h-10 w-28 shrink-0">
                            <button
                              type="button"
                              onClick={() => setAdvancedDatesAllowed((prev) => {
                                if (prev === 'Todos') return 5;
                                if (prev <= 1) return 1;
                                return (prev as number) - 1;
                              })}
                              disabled={advancedDatesAllowed === 1}
                              className="w-7 h-7 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-colors flex items-center justify-center text-white/60 hover:text-white disabled:opacity-20"
                            >
                              <Minus size={10} />
                            </button>
                            <div className="flex flex-col items-center">
                              <span className="text-[11px] font-black text-white/90">{advancedDatesAllowed}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setAdvancedDatesAllowed((prev) => {
                                if (prev === 'Todos') return 'Todos';
                                if (prev >= 5) return 'Todos';
                                return (prev as number) + 1;
                              })}
                              disabled={advancedDatesAllowed === 'Todos'}
                              className="w-7 h-7 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-colors flex items-center justify-center text-white/60 hover:text-white disabled:opacity-20"
                            >
                              <Plus size={10} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-3 pt-2">
                {/* Modern Date Selection (Moved to top) */}
                <div className="bg-white/5 p-4 rounded-2xl border border-white/5 flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-2 px-1">
                    <label className="text-[9px] leading-tight font-black uppercase opacity-40 tracking-widest max-w-[80px]">
                      Fecha de inicio oficial
                    </label>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => {
                          const d = new Date();
                          d.setHours(0, 0, 0, 0);
                          setOfficialStartDate(d.getTime());
                        }}
                        className={`px-2.5 py-1.5 rounded-lg text-[8px] font-black uppercase transition-all ${new Date(officialStartDate).toDateString() === new Date().toDateString() ? 'theme-primary-bg text-black' : 'bg-white/5 hover:bg-white/10 opacity-40'}`}
                      >Hoy</button>
                      <button
                        onClick={() => {
                          const d = new Date(Date.now() + 86400000);
                          d.setHours(0, 0, 0, 0);
                          setOfficialStartDate(d.getTime());
                        }}
                        className={`px-2.5 py-1.5 rounded-lg text-[8px] font-black uppercase transition-all ${new Date(officialStartDate).toDateString() === new Date(Date.now() + 86400000).toDateString() ? 'theme-primary-bg text-black' : 'bg-white/5 hover:bg-white/10 opacity-40'}`}
                      >Mañana</button>

                      <div className="relative">
                        <input
                          type="date"
                          value={new Date(officialStartDate).toISOString().split('T')[0]}
                          onChange={(e) => {
                            if (!e.target.value) return;
                            const d = new Date(e.target.value + 'T00:00:00');
                            setOfficialStartDate(d.getTime());
                          }}
                          onClick={(e) => {
                            try {
                              if ('showPicker' in HTMLInputElement.prototype) {
                                e.currentTarget.showPicker();
                              }
                            } catch (err) {}
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                        />
                        <button
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[8px] font-black uppercase transition-all ${
                            new Date(officialStartDate).toDateString() !== new Date().toDateString() && new Date(officialStartDate).toDateString() !== new Date(Date.now() + 86400000).toDateString()
                              ? 'theme-primary-bg text-black'
                              : 'bg-white/5 hover:bg-white/10 opacity-40'
                          }`}
                        >
                          <CalendarDays size={10} />
                          Elegir fecha
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-center justify-center gap-0.5 py-2.5 bg-black/20 rounded-xl border border-white/5 overflow-hidden">
                    <span className="text-xs sm:text-sm whitespace-nowrap text-ellipsis overflow-hidden font-black uppercase theme-primary-text tracking-widest text-center px-4 max-w-full">
                      {new Date(officialStartDate).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </span>
                    <p className="text-[7px] font-bold opacity-30 uppercase tracking-widest text-center">
                      Los partidos se generarán a partir de este día
                    </p>
                  </div>
                </div>
                {!allApproved && sortedRegisteredTeams.length >= 2 && (
                  <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                    <AlertCircle size={14} className="text-white/90 animate-pulse" />
                    <p className="text-[9px] font-black uppercase text-white/90 tracking-tight">
                      Aprobá a todas las parejas para habilitar el inicio
                    </p>
                  </div>
                )}

                <button
                  disabled={isStarting}
                  onClick={() => {
                    if (sortedRegisteredTeams.length < 2) {
                      setErrorModal({ show: true, message: 'Se necesitan al menos 2 parejas para iniciar' });
                      return;
                    }
                    if (!allApproved) {
                      setErrorModal({ show: true, message: 'Aprobá a todas las parejas para habilitar el inicio' });
                      return;
                    }
                    handleStartProcedure();
                  }}
                  className={`w-full group/start relative h-16 theme-primary-bg text-black font-black rounded-2xl flex flex-col items-center justify-center gap-0.5 uppercase tracking-tighter transition-all overflow-hidden ${isStarting
                    ? 'opacity-40 grayscale cursor-not-allowed'
                    : 'hover:scale-[1.03] active:scale-95'
                    }`}
                >
                  <div className="flex items-center gap-3 relative z-10">
                    <span className="text-sm font-black italic">Comenzar torneo oficial</span>
                    <ArrowRight size={18} className="group-hover/start:translate-x-1 transition-transform" />
                  </div>
                  <span className="text-[8px] font-black opacity-50 tracking-widest uppercase relative z-10">Verás un resumen detallado antes</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showStartConfirmation && (
          <StartConfirmationUI
            t={t}
            sortedRegisteredTeams={sortedRegisteredTeams}
            numZones={numZones}
            qualifiersPerZone={qualifiersPerZone}
            doubleRound={doubleRound}
            officialStartDate={officialStartDate}
            setOfficialStartDate={setOfficialStartDate}
            isStarting={isStarting}
            confirmStartTournament={confirmStartTournament}
            setShowStartConfirmation={setShowStartConfirmation}
            advancedScheduleRestrictions={advancedScheduleRestrictions}
            schedulePlayMode={schedulePlayMode}
            scheduleDaysPerRound={scheduleDaysPerRound}
            scheduleTimeUnit={scheduleTimeUnit}
            advancedDatesAllowed={advancedDatesAllowed}
          />
        )}
        {selectedPlayer && (
          <PlayerDetailModal
            player={selectedPlayer}
            players={players}
            onClose={() => setSelectedPlayer(null)}
            onMatchClick={() => { }}
            onPlayerClick={(id) => {
              const p = players.find(player => player.id === id);
              if (p) setSelectedPlayer(p);
            }}
            config={config}
            forceShowContactInfo={true}
          />
        )}
      </AnimatePresence>

      {showCoupleBuilder && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setShowCoupleBuilder(false)}></div>
          <div className="bg-[#0f172a] border border-theme-primary/30 w-full max-w-md rounded-[2rem] shadow-[0_0_50px_rgba(0,0,0,0.5)] relative z-10 p-6 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-theme-primary/10 border border-theme-primary/20">
                  <Plus size={18} className="theme-primary-text" />
                </div>
                <div>
                  <h5 className="text-[12px] font-black uppercase tracking-widest theme-primary-text">Nueva Pareja</h5>
                  <p className="text-[9px] opacity-40 uppercase tracking-widest mt-0.5">Inscribe una pareja directamente</p>
                </div>
              </div>
              <button onClick={() => setShowCoupleBuilder(false)} className="p-2 text-white/20 hover:text-white hover:bg-white/5 rounded-xl transition-all">
                <X size={16} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="space-y-3">
                <SearchablePlayerSelect
                  value={p1Id}
                  onChange={setP1Id}
                  players={players.filter(p => !registeredTeams.flatMap(rt => [rt.player1Id, rt.player2Id]).includes(p.id) && (!p.isGuest || p.guestTournamentId === t.id))}
                  placeholder="Jugador 1"
                />
                {p1Id && !players.find(p => p.id === p1Id)?.phone && (
                  <input
                    type="tel"
                    placeholder="WhatsApp Jugador 1 (Recomendado)"
                    value={p1PhoneInput}
                    onChange={(e) => setP1PhoneInput(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-3 font-bold text-xs outline-none focus:border-theme-primary/50 text-white"
                  />
                )}
                <SearchablePlayerSelect
                  value={p2Id}
                  onChange={setP2Id}
                  players={players.filter(p => p.id !== p1Id && !registeredTeams.flatMap(rt => [rt.player1Id, rt.player2Id]).includes(p.id) && (!p.isGuest || p.guestTournamentId === t.id))}
                  placeholder="Jugador 2"
                />
                {p2Id && !players.find(p => p.id === p2Id)?.phone && (
                  <input
                    type="tel"
                    placeholder="WhatsApp Jugador 2 (Recomendado)"
                    value={p2PhoneInput}
                    onChange={(e) => setP2PhoneInput(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-3 font-bold text-xs outline-none focus:border-theme-primary/50 text-white"
                  />
                )}
              </div>
              <button
                onClick={handleCreateCoupleAndRegister}
                disabled={isCreatingCouple || !p1Id || !p2Id}
                className="w-full theme-primary-bg text-black font-black h-12 rounded-xl uppercase text-[10px] tracking-widest disabled:opacity-20 shadow-xl transition-all hover:brightness-110 active:scale-95 mt-4"
              >
                {isCreatingCouple ? 'Registrando...' : 'Confirmar Registro'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showAddGuest && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setShowAddGuest(false)}></div>
          <div className="bg-[#0f172a] border border-blue-500/30 w-full max-w-md rounded-[2rem] shadow-[0_0_50px_rgba(0,0,0,0.5)] relative z-10 p-6 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20">
                  <Users size={18} className="text-blue-400" />
                </div>
                <div>
                  <h5 className="text-[12px] font-black uppercase tracking-widest text-blue-400">Invitado Externo</h5>
                  <p className="text-[9px] opacity-40 uppercase tracking-widest mt-0.5">Agrega un jugador sin cuenta</p>
                </div>
              </div>
              <button onClick={() => setShowAddGuest(false)} className="p-2 text-white/20 hover:text-white hover:bg-white/5 rounded-xl transition-all">
                <X size={16} />
              </button>
            </div>

            <div className="space-y-5">
              <div className="space-y-3">
                <div className="relative group/input">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 opacity-20 group-focus-within/input:opacity-100 group-focus-within/input:text-blue-400 transition-all">
                    <User size={16} />
                  </div>
                  <input
                    type="text"
                    placeholder="Nombre y Apellido"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-xs font-bold uppercase outline-none focus:border-blue-400/50 focus:bg-white/[0.06] transition-all placeholder:opacity-30"
                  />
                </div>
                <div className="relative group/input">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 opacity-20 group-focus-within/input:opacity-100 group-focus-within/input:text-blue-400 transition-all">
                    <Phone size={16} />
                  </div>
                  <input
                    type="tel"
                    placeholder="Teléfono (Opcional)"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-xs font-bold uppercase outline-none focus:border-blue-400/50 focus:bg-white/[0.06] transition-all placeholder:opacity-30"
                  />
                </div>
              </div>
              <button
                onClick={handleCreateGuest}
                disabled={isCreatingGuest || !guestName.trim()}
                className="w-full relative h-12 flex items-center justify-center gap-3 bg-blue-500/80 hover:bg-blue-400 text-white rounded-xl font-black uppercase text-[10px] tracking-widest transition-all shadow-[0_10px_30px_rgba(59,130,246,0.2)] hover:scale-[1.02] active:scale-95 disabled:opacity-20 overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/10 translate-y-full hover:translate-y-0 transition-transform duration-300" />
                <span className="relative z-10">{isCreatingGuest ? 'Creando...' : 'Registrar Invitado'}</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}

interface StartConfirmationUIProps {
  t: Tournament;
  sortedRegisteredTeams: Team[];
  numZones: number;
  qualifiersPerZone: number;
  doubleRound: boolean;
  officialStartDate: number;
  setOfficialStartDate: (d: number) => void;
  isStarting: boolean;
  confirmStartTournament: () => void;
  setShowStartConfirmation: (b: boolean) => void;
  advancedScheduleRestrictions: boolean;
  schedulePlayMode: 'open' | 'restricted';
  scheduleDaysPerRound: number;
  scheduleTimeUnit?: 'days' | 'minutes' | 'hours';
  advancedDatesAllowed?: number | 'Todos';
}

const StartConfirmationUI = ({
  t,
  sortedRegisteredTeams,
  numZones,
  qualifiersPerZone,
  doubleRound,
  officialStartDate,
  setOfficialStartDate,
  isStarting,
  confirmStartTournament,
  setShowStartConfirmation,
  advancedScheduleRestrictions,
  schedulePlayMode,
  scheduleDaysPerRound,
  scheduleTimeUnit = 'days',
  advancedDatesAllowed
}: StartConfirmationUIProps) => {
  const isLiga = t.type === 'liga';

  const teamsPerZone = Math.ceil(sortedRegisteredTeams.length / numZones);
  const baseRounds = teamsPerZone % 2 === 0 ? teamsPerZone - 1 : teamsPerZone;
  const totalDates = doubleRound ? baseRounds * 2 : baseRounds;

  const getMatchCount = () => {
    let count = 0;
    for (let i = 0; i < numZones; i++) {
      const base = Math.floor(sortedRegisteredTeams.length / numZones);
      const extra = i < (sortedRegisteredTeams.length % numZones) ? 1 : 0;
      const n = base + extra;
      count += (n * (n - 1) / 2);
    }
    return doubleRound ? count * 2 : count;
  };

  const totalMatches = isLiga ? getMatchCount() : 0;
  const estimatedEndDate = officialStartDate + totalDates * 7 * 24 * 60 * 60 * 1000;
  const hasFreeDates = () => {
    for (let i = 0; i < numZones; i++) {
      const base = Math.floor(sortedRegisteredTeams.length / numZones);
      const extra = i < (sortedRegisteredTeams.length % numZones) ? 1 : 0;
      if ((base + extra) % 2 !== 0) return true;
    }
    return false;
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={() => setShowStartConfirmation(false)}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative w-full max-w-md bg-slate-800 border border-white/10 rounded-[32px] shadow-2xl overflow-y-auto max-h-[90vh] custom-scrollbar"
      >
        <div className="p-6 space-y-5">
          {/* Minimal Header */}
          <div className="flex items-center justify-between px-1">
            <h3 className="text-xs font-black uppercase italic tracking-wider opacity-80">Resumen del Torneo</h3>
            <div className="px-2 py-0.5 bg-theme-primary/10 rounded-md border border-theme-primary/20">
              <span className="text-[8px] font-black uppercase theme-primary-text italic">
                {t.managementMode === 'express' ? 'Torneo Express • ' : ''}{t.type === 'liga' ? 'Formato Liga' : 'Formato Eliminación'}
              </span>
            </div>
          </div>

          <div className="space-y-4">

            {/* Detailed Summary Card */}
            <div className="p-4 bg-white/5 rounded-2xl border border-white/5 space-y-3">
              <div className="space-y-2">
                {t.type === 'liga' ? (
                  <>
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase pb-1 border-b border-white/5 mb-1">
                      <Users size={12} className="theme-primary-text" />
                      <span className="theme-primary-text">{sortedRegisteredTeams.length} Parejas Registradas</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase">
                      <div className="w-1.5 h-1.5 rounded-full theme-primary-bg" />
                      <span className="opacity-80">
                        Inicia: {new Date(officialStartDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase">
                      <div className="w-1.5 h-1.5 rounded-full theme-primary-bg" />
                      <span className="opacity-80">{numZones} zonas • {qualifiersPerZone} clasifican por zona</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase">
                      <div className="w-1.5 h-1.5 rounded-full theme-primary-bg" />
                      <span className="opacity-80">
                        {doubleRound ? 'Ida y Vuelta' : 'Solo Ida'} • {totalDates} Fechas ({totalMatches} partidos totales)
                      </span>
                    </div>
                    {t.managementMode !== 'express' && (
                      <>
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase">
                          <div className="w-1.5 h-1.5 rounded-full theme-primary-bg" />
                          <span className="opacity-80">
                            Duración de fecha: {scheduleDaysPerRound} días
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase">
                          <div className="w-1.5 h-1.5 rounded-full theme-primary-bg" />
                          <span className="opacity-80">
                            Fin fase de liga: {new Date(estimatedEndDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                          </span>
                        </div>
                      </>
                    )}
                    {sortedRegisteredTeams.length % numZones !== 0 && (
                      <div className="p-2.5 bg-blue-500/10 border border-blue-500/20 rounded-xl mt-1">
                        <p className="text-[8px] font-bold text-blue-300/60 leading-relaxed uppercase italic">
                          ⚠️ Zonas desiguales detectadas: El sistema sorteará de forma automatica la cantidad de parejas por zonas. Pero quedarán desniveladas
                        </p>
                      </div>
                    )}
                    {hasFreeDates() && (
                      <div className="p-2.5 bg-slate-500/10 border border-slate-500/20 rounded-xl mt-1">
                        <p className="text-[8px] font-bold text-slate-300/60 leading-relaxed uppercase italic">
                          📅 Fecha Libre: Debido a la cantidad impar de equipos en algunas zonas, cada equipo de esa zona tendrá una fecha libre durante el torneo.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase pb-1 border-b border-white/5 mb-1">
                      <Users size={12} className="theme-primary-text" />
                      <span className="theme-primary-text">{sortedRegisteredTeams.length} Parejas Registradas</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase">
                      <div className="w-1.5 h-1.5 rounded-full theme-primary-bg" />
                      <span className="opacity-80">{sortedRegisteredTeams.length} equipos en cuadro directo</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase">
                      <div className="w-1.5 h-1.5 rounded-full theme-primary-bg" />
                      <span className="opacity-80">
                        {(() => {
                          const n = sortedRegisteredTeams.length;
                          let pow2 = 1; while (pow2 < n) pow2 *= 2;
                          const byes = pow2 - n;
                          return byes > 0
                            ? `${byes} equipos con Pase Directo (BYE)`
                            : 'Cuadro perfecto (sin BYEs)';
                        })()}
                      </span>
                    </div>
                    {t.managementMode !== 'express' && (
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase">
                        <div className="w-1.5 h-1.5 rounded-full theme-primary-bg" />
                        <span className="opacity-80">
                          Duración de fecha: {scheduleDaysPerRound} días
                        </span>
                      </div>
                    )}
                    {(() => {
                      const n = sortedRegisteredTeams.length;
                      let pow2 = 1; while (pow2 < n) pow2 *= 2;
                      return pow2 - n > 0;
                    })() && (
                        <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl mt-1">
                          <p className="text-[8px] font-bold text-amber-200/60 leading-relaxed uppercase italic">
                            💡 Los "Pases Directos" se asignarán por sorteo para completar la llave de potencia de 2.
                          </p>
                        </div>
                      )}
                  </>
                )}
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase italic theme-primary-text/60">
                  <CheckCircle2 size={10} />
                  <span>Los encuentros se sortearán al azar automáticamente</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <button
              onClick={confirmStartTournament}
              disabled={isStarting}
              className="w-full h-12 theme-primary-bg text-black font-black rounded-xl uppercase text-xs tracking-widest shadow-xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
            >
              {isStarting ? 'Lanzando...' : 'Confirmar e Iniciar'}
            </button>
            <button
              onClick={() => setShowStartConfirmation(false)}
              className="w-full h-10 text-white/30 font-black rounded-xl uppercase text-[9px] tracking-widest hover:text-white transition-all"
            >
              Cancelar
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
